'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MarkdownRenderer } from '@components/ui/MarkdownRenderer';
import { useAnnouncements } from '@hooks/useAnnouncements';
import type { Announcement } from '@/types/app/announcements';

interface AnnouncementBannerProps {
  className?: string;
}

const typeIcons = {
  feature: '✨',
  update: 'ℹ️',
  maintenance: '🔧',
  warning: '⚠️',
  info: 'ℹ️'
};

const typeColors = {
  feature: 'bg-gradient-to-br from-green-50 to-blue-50 border-green-200 text-green-800',
  update: 'bg-blue-50 border-blue-200 text-blue-800',
  maintenance: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  warning: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-gray-50 border-gray-200 text-gray-800'
};

const priorityColors = {
  critical: 'border-l-red-500',
  high: 'border-l-orange-500',
  medium: 'border-l-blue-500',
  low: 'border-l-gray-500'
};

function AnnouncementCard({ 
  announcement, 
  onDismiss, 
  onMarkAsRead, 
  isRead 
}: {
  announcement: Announcement;
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  isRead: boolean;
}) {
  const [timeLeft, setTimeLeft] = useState(announcement.readingTimeSeconds);
  const [canDismiss, setCanDismiss] = useState(!announcement.requiresReading || isRead);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const iconEmoji = typeIcons[announcement.type];

  useEffect(() => {
    if (announcement.requiresReading && !isRead && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setCanDismiss(true);
            onMarkAsRead(announcement.id);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [announcement.requiresReading, isRead, timeLeft, onMarkAsRead, announcement.id]);

  const handleDismiss = () => {
    if (canDismiss) {
      onDismiss(announcement.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`
        relative border-l-4 rounded-lg shadow-lg backdrop-blur-sm
        ${typeColors[announcement.type]}
        ${priorityColors[announcement.priority]}
        p-4 sm:p-6 mb-4 max-w-4xl mx-auto
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-xl flex-shrink-0">{iconEmoji}</span>
          <div>
            <h3 className="font-semibold text-base sm:text-lg leading-tight pr-2">
              {announcement.title}
            </h3>
            <div className="flex flex-wrap items-center gap-x-2 mt-1 text-xs sm:text-sm opacity-75">
              <span className="whitespace-nowrap">v{announcement.version}</span>
              <span className="hidden sm:inline">•</span>
              <span className="capitalize hidden sm:inline">{announcement.type}</span>
              {announcement.priority === 'critical' && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="font-medium text-red-600">Critical</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          disabled={!canDismiss}
          className={`
            p-2 sm:p-1 rounded-full transition-all duration-200 -mt-1 -mr-1
            ${canDismiss 
              ? 'hover:bg-black/10 text-current cursor-pointer' 
              : 'opacity-30 cursor-not-allowed'
            }
          `}
          title={canDismiss ? 'Dismiss announcement' : `Please read for ${timeLeft} more seconds`}
        >
          <span className="text-xl sm:text-lg">✕</span>
        </button>
      </div>

      {/* Content */}
      <div className="prose prose-sm max-w-none px-2 sm:px-0">
        <MarkdownRenderer>{announcement.content}</MarkdownRenderer>
      </div>

      {/* Reading timer */}
      {announcement.requiresReading && !isRead && timeLeft > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center mt-4 p-3 bg-black/5 rounded-md text-center"
        >
          <span className="mr-2 text-lg">🕐</span>
          <span className="text-xs sm:text-sm font-medium">
            Please read for {timeLeft} more second{timeLeft !== 1 ? 's' : ''} before dismissing
          </span>
        </motion.div>
      )}

      {/* Read confirmation */}
      {announcement.requiresReading && isRead && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center mt-4 p-2 bg-green-100 text-green-700 rounded-md"
        >
          <span className="mr-2">✅</span>
          <span className="text-sm font-medium">Thank you for reading!</span>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function AnnouncementBanner({ className = '' }: AnnouncementBannerProps) {
  const { 
    announcements, 
    loading, 
    error, 
    dismissAnnouncement, 
    markAsRead, 
    isAnnouncementRead 
  } = useAnnouncements();

  if (loading || error || announcements.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      <div className="bg-gradient-to-r from-blue-50 to-green-50 border-b-2 border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <AnimatePresence mode="wait">
            {announcements.slice(0, 1).map(announcement => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                onDismiss={dismissAnnouncement}
                onMarkAsRead={markAsRead}
                isRead={isAnnouncementRead(announcement.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
} 